// v50.309 — MobileNavSheet (full Apple-style platform nav).
//
// Opened by the centre FAB on MobileBottomNav. Replaces the previous
// "More destinations" flat list with a full-platform navigation sheet
// that mirrors what desktop users get from the Sidebar.
//
// Layout per Sam's brief:
//   • Apple sheet chrome — frosted backdrop, 22px top radius, hairline
//     separators, grouped sections, sentence case throughout.
//   • Every primary nav area for the role appears as a top-level row.
//   • Areas with secondary items get an expandable dropdown — tap the
//     row to expand inline; tap the chevron-row title text to navigate
//     to the area's primary route.
//   • Sub-items group under quiet sentence-case headers (matches
//     desktop secondary-rail grouping from groupSecondary()).
//   • Hybrid users get a View-as switch at the foot.
//
// Mobile-only. Desktop renders Sidebar; this file is never mounted
// at sm+ breakpoints.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useShellUser } from "@/features/auth/AuthContext";
import { adminTierOf } from "@/features/admin/components/AdminGate";
import { useEffectiveRole, useRoleOverride, type RoleOverride } from "../roleOverride";
import { railFor, groupSecondary, ADMIN_NAV, type NavArea, type SecondaryItem } from "../navAreas";
import { Icon } from "@/components/Icon";
import type { AdminTier } from "@/lib/api/types";

interface Props { open: boolean; onClose: () => void }

export function MobileNavSheet({ open, onClose }: Props) {
  const navigate = useNavigate();
  const user     = useShellUser();
  const role     = useEffectiveRole();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  /* Close on Escape. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /* Reset expansion state when sheet closes so reopening starts clean. */
  useEffect(() => {
    if (!open) setExpanded(new Set());
  }, [open]);

  if (!open || !user) return null;
  const areas = railFor(role);
  const adminTier = adminTierOf(user);

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end"
      style={{
        background: "rgba(8,17,33,.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={onClose}
      onPointerDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* v50.312 — Sheet now uses a FIXED 85dvh height instead of a
          variable max-height. The variable max-height was the root of
          the broken scroll: a flex-col container sized to its content
          gives flex-1 children no defined bound to claim, so
          overflow-y-auto on the body didn't kick in reliably on
          mobile browsers — the expanded sub-items extended past the
          viewport and the sheet's overflow-hidden simply clipped
          them. With a fixed dynamic-viewport height the body always
          has a hard bound to scroll within. */}
      <div
        className="lc-safe-pb w-full bg-white shadow-2xl flex flex-col overflow-hidden"
        style={{
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          height: "85dvh",
          maxHeight: "85vh",
        }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Platform navigation"
      >
        {/* Grabber */}
        <div className="pt-2.5 pb-1 flex justify-center shrink-0">
          <div className="w-9 h-1 rounded-full" style={{ background: "rgba(60,60,67,0.13)" }} />
        </div>

        {/* Header */}
        <div
          className="px-5 pt-2 pb-3 flex items-center justify-between shrink-0"
          style={{ borderBottom: "0.5px solid rgba(60,60,67,0.13)" }}
        >
          <div>
            <div className="text-[18px] font-semibold text-lc-ink tracking-tight">Menu</div>
            <div className="text-[12px] text-lc-mute mt-0.5">Tap a section to expand or jump straight to it.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="w-8 h-8 rounded-full bg-lc-bg-2 text-lc-mute hover:text-lc-ink flex items-center justify-center shrink-0"
          >
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* v50.312 — Scrollable nav body. flex+min-h-0 plus the inline
            style belt-and-braces the iOS Safari flex-overflow quirk:
            tailwind's flex-1 generated declaration occasionally fails
            to take effect inside fixed-height columns at the first
            paint. Inline style guarantees the flex sizing. */}
        <div
          className="px-3 py-2 flex flex-col"
          style={{
            flex: "1 1 0",
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
          }}
        >
          <div className="flex flex-col gap-0.5">
            {areas.map(area => (
              <NavAreaRow
                key={area.id}
                area={area}
                expanded={expanded.has(area.id)}
                onToggle={() => toggle(area.id)}
                onNavigate={go}
              />
            ))}

            {/* v50.324 — collapsible Admin section, mirroring the desktop
                sidebar's per-tier link set. */}
            {adminTier && (
              <AdminNavRow
                tier={adminTier}
                expanded={expanded.has("__admin__")}
                onToggle={() => toggle("__admin__")}
                onNavigate={go}
              />
            )}

            {user.role === "hybrid" && <MobileViewAsSwitch />}

            {/* v50.324 — profile + sign-out footer. Mobile previously had
                no route to the profile or a sign-out control at all
                (desktop carries both in the Sidebar's ProfileMenu). */}
            <MobileProfileFooter onNavigate={go} />
            {/* Tail spacer so the very last item isn't hidden behind
                the iOS home-indicator inset / keyboard. */}
            <div style={{ height: 24 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── single area row with optional expansion ─────────────────────── */

function NavAreaRow({
  area, expanded, onToggle, onNavigate,
}: {
  area:       NavArea;
  expanded:   boolean;
  onToggle:   () => void;
  onNavigate: (path: string) => void;
}) {
  const hasSecondary = !!area.secondary && area.secondary.length > 0;
  const rowRef = useRef<HTMLDivElement | null>(null);

  /* v50.310 — when a row expands, scroll it into view so the
     newly-revealed sub-nav lands on-screen instead of being below
     the fold of the scrollable sheet body. Skipped on first render
     so opening the sheet doesn't auto-scroll if a section is
     pre-expanded. v50.312 — removed behavior: smooth because iOS
     Safari can get stuck mid-animation when the scroll container
     also re-layouts; instant scroll lands consistently. */
  const didExpandRef = useRef(false);
  useEffect(() => {
    if (!expanded) { didExpandRef.current = false; return; }
    if (didExpandRef.current) return;
    didExpandRef.current = true;
    const el = rowRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      try { el.scrollIntoView({ block: "nearest" }); } catch { /* fallback */ }
    });
  }, [expanded]);

  /* Tap row body → navigate to area's main route + close sheet.
     Tap the chevron alone → expand/collapse without navigating. */
  return (
    <div ref={rowRef} className="rounded-[12px] overflow-hidden">
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => onNavigate(area.route)}
          className="flex-1 text-left px-3 py-3 flex items-center gap-3 hover:bg-lc-bg-2 active:bg-lc-blue-light/40 transition-colors"
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          <span
            aria-hidden="true"
            className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
            style={{ background: "var(--color-lc-blue-light)", color: "var(--color-lc-blue-dark)" }}
          >
            <Icon name={area.icon} size={17} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-semibold text-lc-ink tracking-[-0.005em]">{area.label}</span>
            {hasSecondary && (
              <span className="block text-[11.5px] text-lc-faint mt-0.5">
                {area.secondary!.length} section{area.secondary!.length === 1 ? "" : "s"}
              </span>
            )}
          </span>
        </button>
        {hasSecondary && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? "Collapse section" : "Expand section"}
            aria-expanded={expanded}
            className="px-3 flex items-center justify-center hover:bg-lc-bg-2 transition-colors"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center transition-transform"
              style={{
                background: expanded ? "var(--color-lc-blue-light)" : "transparent",
                color: expanded ? "var(--color-lc-blue-dark)" : "var(--color-lc-faint)",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <Icon name="chevron-down" size={14} />
            </span>
          </button>
        )}
      </div>

      {/* v50.312 — Sub-nav reverted to simple text rows per Sam's
          preference (the v50.311 white cards + per-row borders read
          as cluttered). Layout primitive stays plain <div> (not
          <ul>/<li>) so the iOS Safari list-item-in-flex sizing bug
          doesn't return. Items are full-width tappable buttons with
          guaranteed minHeight so heights stack cleanly. The expanded
          region itself just gets a hairline top border to mark it
          as a sub-section, no background tint. */}
      {hasSecondary && expanded && (
        <div
          className="pt-1 pb-2 pl-12 pr-2"
          style={{ borderTop: "0.5px solid rgba(60,60,67,0.08)" }}
        >
          {groupSecondary(area.secondary!).map(({ group, items }, gi) => (
            <div key={group || `__g${gi}__`} className={gi > 0 ? "mt-2" : ""}>
              {group && (
                <div className="text-[11px] font-semibold uppercase tracking-[0.04em] text-lc-faint px-2 py-1.5">
                  {group}
                </div>
              )}
              <div className="flex flex-col">
                {items.map(it => <SecondaryRow key={it.id} item={it} onNavigate={onNavigate} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SecondaryRow({ item, onNavigate }: { item: SecondaryItem; onNavigate: (path: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.route)}
      className="w-full text-left px-2 py-2 rounded-[8px] flex items-center gap-2 hover:bg-lc-bg-2 active:bg-lc-blue-light/30 transition-colors"
      style={{
        WebkitTapHighlightColor: "transparent",
        minHeight: 40,
      }}
    >
      <span className="flex-1 min-w-0 text-[13.5px] text-lc-ink-2 font-medium truncate">{item.label}</span>
      <span className="text-lc-faint shrink-0">
        <Icon name="chevron-right" size={12} />
      </span>
    </button>
  );
}

/* ── collapsible admin section ────────────────────────────────── */

function AdminNavRow({
  tier, expanded, onToggle, onNavigate,
}: {
  tier:       AdminTier;
  expanded:   boolean;
  onToggle:   () => void;
  onNavigate: (path: string) => void;
}) {
  const nav = ADMIN_NAV[tier];
  return (
    <div className="rounded-[12px] overflow-hidden mt-2" style={{ borderTop: "0.5px solid rgba(60,60,67,0.13)" }}>
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => onNavigate(nav.links[0].to)}
          className="flex-1 text-left px-3 py-3 flex items-center gap-3 hover:bg-lc-bg-2 active:bg-lc-blue-light/40 transition-colors"
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          <span
            aria-hidden="true"
            className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
            style={{ background: "var(--color-lc-blue-light)", color: "var(--color-lc-blue-dark)" }}
          >
            <Icon name="shield" size={17} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-semibold text-lc-ink tracking-[-0.005em]">{nav.label}</span>
            <span className="block text-[11.5px] text-lc-faint mt-0.5">
              {nav.links.length} section{nav.links.length === 1 ? "" : "s"}
            </span>
          </span>
        </button>
        {nav.links.length > 1 && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? "Collapse section" : "Expand section"}
            aria-expanded={expanded}
            className="px-3 flex items-center justify-center hover:bg-lc-bg-2 transition-colors"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center transition-transform"
              style={{
                background: expanded ? "var(--color-lc-blue-light)" : "transparent",
                color: expanded ? "var(--color-lc-blue-dark)" : "var(--color-lc-faint)",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <Icon name="chevron-down" size={14} />
            </span>
          </button>
        )}
      </div>
      {nav.links.length > 1 && expanded && (
        <div className="pt-1 pb-2 pl-12 pr-2" style={{ borderTop: "0.5px solid rgba(60,60,67,0.08)" }}>
          <div className="flex flex-col">
            {nav.links.map(l => (
              <button
                key={l.to}
                type="button"
                onClick={() => onNavigate(l.to)}
                className="w-full text-left px-2 py-2 rounded-[8px] flex items-center gap-2 hover:bg-lc-bg-2 active:bg-lc-blue-light/30 transition-colors"
                style={{ WebkitTapHighlightColor: "transparent", minHeight: 40 }}
              >
                <span className="flex-1 min-w-0 text-[13.5px] text-lc-ink-2 font-medium truncate">{l.label}</span>
                <span className="text-lc-faint shrink-0">
                  <Icon name="chevron-right" size={12} />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── profile + sign-out footer ────────────────────────────────── */

function MobileProfileFooter({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { user, signOut } = useAuth();
  if (!user) return null;
  return (
    <div className="mt-2 pt-2 px-1" style={{ borderTop: "0.5px solid rgba(60,60,67,0.13)" }}>
      <button
        type="button"
        onClick={() => onNavigate("/me")}
        className="w-full text-left px-2 py-2.5 rounded-[12px] flex items-center gap-3 hover:bg-lc-bg-2 active:bg-lc-blue-light/40 transition-colors"
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        <span className="w-9 h-9 rounded-full bg-gradient-to-br from-lc-navy to-lc-blue-dark text-white font-extrabold text-xs flex items-center justify-center shrink-0">
          {user.initials}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14px] font-semibold text-lc-ink truncate">{user.name}</span>
          <span className="block text-[11.5px] text-lc-faint mt-0.5">View profile</span>
        </span>
        <span className="text-lc-faint shrink-0">
          <Icon name="chevron-right" size={13} />
        </span>
      </button>
      <button
        type="button"
        onClick={() => onNavigate("/settings")}
        className="w-full text-left px-2 py-2.5 rounded-[12px] flex items-center gap-3 hover:bg-lc-bg-2 transition-colors"
        style={{ WebkitTapHighlightColor: "transparent", minHeight: 44 }}
      >
        <span className="w-9 h-9 rounded-[10px] bg-lc-bg-2 text-lc-mute flex items-center justify-center shrink-0">
          <Icon name="tools" size={16} />
        </span>
        <span className="flex-1 min-w-0 text-[14px] font-semibold text-lc-ink-2">Account settings</span>
      </button>
      <button
        type="button"
        onClick={() => signOut()}
        className="w-full text-left px-2 py-2.5 rounded-[12px] flex items-center gap-3 hover:bg-lc-red-bg transition-colors"
        style={{ WebkitTapHighlightColor: "transparent", minHeight: 44 }}
      >
        <span className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: "rgba(185,28,28,0.08)", color: "#B91C1C" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </span>
        <span className="flex-1 min-w-0 text-[14px] font-semibold" style={{ color: "#B91C1C" }}>Sign out</span>
      </button>
    </div>
  );
}

/* ── hybrid View-as switch (foot) ─────────────────────────────── */

function MobileViewAsSwitch() {
  const [override, setOverride] = useRoleOverride();
  const opts: { value: RoleOverride; label: string }[] = [
    { value: null,          label: "Both" },
    { value: "coordinator", label: "Coord" },
    { value: "partner",     label: "Partner" },
  ];
  return (
    <div className="mt-2 pt-3 px-2" style={{ borderTop: "0.5px solid rgba(60,60,67,0.13)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-lc-faint mb-1.5 px-1">View as</div>
      <div className="grid grid-cols-3 gap-1 bg-lc-bg-2 rounded-[10px] p-0.5">
        {opts.map(o => {
          const active = override === o.value;
          return (
            <button
              key={o.label}
              type="button"
              onClick={() => setOverride(o.value)}
              className="px-2 py-2 rounded-[7px] text-[12.5px] font-semibold transition-colors"
              style={{
                background: active ? "#fff" : "transparent",
                color: active ? "var(--color-lc-blue-dark)" : "var(--color-lc-mute)",
                boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
